/**
 * Task-oriented primary navigation for the TB Mukt Bharat portal.
 * Designed around "what does the visitor want to do?", NOT departments/schemes.
 *
 * Each item carries an i18n key (nav.*) so labels render in EN/HI/MR.
 * The "Community" entry links to the retained social features (Feed/Groups/etc.)
 * per the agreed hybrid scope — portal-first, community preserved.
 *
 * Text (labels/descriptions) lives in i18n locales; only non-text structure
 * (keys, paths, icons, tones) lives here. HomePage resolves audience text via
 * t('audiences.<key>.label' / '.desc' / '.linkLabels').
 */

export interface NavItem {
  /** i18n key under `nav` namespace */
  key: string
  /** fallback English label (used if translation missing) */
  label: string
  path: string
  /** optional children for a dropdown (kept to one level max) */
  children?: { key: string; label: string; path: string; desc?: string }[]
  /** if true, render ONLY in the mobile drawer — never in the desktop nav row.
      Used for low-priority destinations (e.g. Contact) that already live in the
      utility bar on desktop but need a touch target on mobile. */
  mobileOnly?: boolean
}

export const PRIMARY_NAV: NavItem[] = [
  { key: 'aboutTb', label: 'About TB', path: '/about-tb' },
  {
    key: 'symptoms',
    label: 'Symptoms',
    path: '/symptom-checker',
    children: [
      { key: 'symptomChecker', label: 'Symptom Checker', path: '/symptom-checker' },
      { key: 'riskChecker', label: 'Am I at Risk?', path: '/am-i-at-risk' },
    ],
  },
  { key: 'iHaveTb', label: 'I Have TB', path: '/i-have-tb' },
  { key: 'getHelp', label: 'Get Help', path: '/get-help' },
  {
    key: 'support',
    label: 'Support',
    path: '/support-a-patient',
    children: [
      { key: 'supportAPatient', label: 'Support a Patient', path: '/support-a-patient' },
      { key: 'becomeSupporter', label: 'Become a Supporter', path: '/become-a-supporter' },
      { key: 'ngoHub', label: 'NGO Hub', path: '/ngo' },
      { key: 'volunteerHub', label: 'Volunteer Hub', path: '/volunteer' },
    ],
  },
  { key: 'findTbCentre', label: 'Find a TB Centre', path: '/find-tb-centre' },
  {
    key: 'resources',
    label: 'Resources',
    path: '/resources',
    children: [
      { key: 'resources', label: 'Resource Library', path: '/resources' },
      { key: 'videos', label: 'Videos', path: '/videos' },
      { key: 'stories', label: 'Stories', path: '/stories' },
      { key: 'news', label: 'News & Updates', path: '/news' },
      { key: 'assistant', label: 'TB Assistant', path: '/assistant' },
      { key: 'campaigns', label: 'Campaigns', path: '/campaigns' },
    ],
  },
  {
    key: 'communityPortal',
    label: 'Community',
    path: '/community',
    children: [
      { key: 'feed', label: 'Community Feed', path: '/feed', desc: 'Stories, posts & support' },
      { key: 'groups', label: 'Support Groups', path: '/groups', desc: 'TB communities' },
      { key: 'messages', label: 'Messages', path: '/messages', desc: 'Direct support chat' },
      { key: 'knowledge', label: 'Knowledge Hub', path: '/knowledge', desc: 'TB awareness articles' },
    ],
  },
  // Contact stays in the utility bar (top-right) — not duplicated here.
  // It is added below as mobileOnly so the mobile drawer still reaches it.
  { key: 'contact', label: 'Contact', path: '/contact', mobileOnly: true },
]

/**
 * Audience definitions for the homepage "I am a…" grid.
 * Text (label/desc/link labels) is resolved from i18n (`audiences.<key>.*`);
 * only the non-text structure is kept here. `links[].path` is zipped by index
 * with the `audiences.<key>.linkLabels` array from the active locale.
 */
export interface Audience {
  key: string
  path: string
  icon: string // lucide icon name resolved in HomePage
  tone: 'blue' | 'green' | 'teal' | 'amber' | 'slate'
  links: { path: string }[]
}

export const AUDIENCES: Audience[] = [
  {
    key: 'patient',
    path: '/i-have-tb',
    icon: 'Stethoscope',
    tone: 'blue',
    links: [
      { path: '/i-have-tb' },
      { path: '/i-have-tb#treatment' },
      { path: '/i-have-tb#nutrition' },
      { path: '/i-have-tb#rights' },
    ],
  },
  {
    key: 'family',
    path: '/support-a-patient',
    icon: 'HeartHandshake',
    tone: 'green',
    links: [
      { path: '/support-a-patient' },
      { path: '/support-a-patient#nutrition' },
      { path: '/support-a-patient#emotional' },
    ],
  },
  {
    key: 'nikshayMitra',
    path: '/support-a-patient#nikshay-mitra',
    icon: 'HandHeart',
    tone: 'teal',
    links: [
      { path: '/support-a-patient#nikshay-mitra' },
      { path: '/support-a-patient' },
      { path: '/about-tb' },
    ],
  },
  {
    key: 'volunteer',
    path: '/get-help',
    icon: 'Users',
    tone: 'amber',
    links: [
      { path: '/get-help' },
      { path: '/resources' },
      { path: '/campaigns' },
    ],
  },
  {
    key: 'student',
    path: '/resources',
    icon: 'GraduationCap',
    tone: 'teal',
    links: [
      { path: '/about-tb' },
      { path: '/resources?type=report' },
      { path: '/resources?type=publication' },
    ],
  },
  {
    key: 'media',
    path: '/resources',
    icon: 'Newspaper',
    tone: 'green',
    links: [
      { path: '/resources?type=media' },
      { path: '/about-tb#facts' },
      { path: '/campaigns' },
    ],
  },
]